import React from "react";
import {
  Alert,
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import LinkIcon from "@mui/icons-material/Link";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import DescriptionIcon from "@mui/icons-material/Description";
import LocationSearchingIcon from "@mui/icons-material/LocationSearching";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { setToastMessage } from "../../store/slices/app/appSlice";
import { AssociatedCampaignAsset } from "../../store/apis/briefingApi";

interface AssociatedCampaignAssetSectionProps {
  asset: AssociatedCampaignAsset | null;
  onCreateAsset?: () => void;
  onEditAsset?: () => void;
  isEditMode?: boolean;
}

const AssociatedCampaignAssetSection: React.FC<
  AssociatedCampaignAssetSectionProps
> = ({ asset, onCreateAsset, onEditAsset, isEditMode = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const handleCopy = (value?: string | null) => {
    if (!value) return;

    navigator.clipboard.writeText(value);
    dispatch(
      setToastMessage({
        message: t("common.copied", "Copied to clipboard!"),
        severity: "success",
      })
    );
  };

  const renderDetailField = (label: string, value?: string | null) => (
    <Grid item xs={12} md={6} key={label}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          transition: "box-shadow 0.2s",
          "&:hover": { boxShadow: theme.shadows[2] },
          bgcolor: theme.palette.background.default,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            mt: "auto",
            color:
              value && value.trim().length > 0
                ? "inherit"
                : theme.palette.text.secondary,
          }}
        >
          {value && value.trim().length > 0
            ? value
            : t("common.noData", "No data available")}
        </Typography>
      </Paper>
    </Grid>
  );

  const renderDestinations = (
    destinations: AssociatedCampaignAsset["destinations"]
  ) => (
    <Grid item xs={12} key="destinations">
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          bgcolor: theme.palette.background.default,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          color="primary.main"
          sx={{ mb: 1.5 }}
        >
          <LinkIcon fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600}>
            {t("briefingsView.asset.destinations", "Destination URLs")}
          </Typography>
          {destinations.length > 0 && (
            <Chip
              size="small"
              label={destinations.length}
              sx={{ height: 20, fontWeight: 700 }}
            />
          )}
        </Stack>

        {destinations.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("common.noData", "No data available")}
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />} spacing={1.5}>
            {destinations.map((destination, index) => (
              <Box key={destination.id ?? index} sx={{ pt: index ? 1.5 : 0 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor:
                        index === 0
                          ? "primary.main"
                          : theme.palette.action.selected,
                      color:
                        index === 0 ? "primary.contrastText" : "text.secondary",
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {destination.label ||
                      t("briefingsView.asset.untitledDestination", "Untitled")}
                  </Typography>
                  {index === 0 && (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={t(
                        "briefingsView.asset.primaryDestination",
                        "Main"
                      )}
                      sx={{ height: 20 }}
                    />
                  )}
                  {!destination.isAutoUtm && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t("briefingsView.asset.manualUtm", "Manual UTM")}
                      sx={{ height: 20 }}
                    />
                  )}
                </Stack>

                <Stack spacing={0.25} sx={{ pl: 3.75 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      component={destination.url ? "a" : "span"}
                      href={destination.url ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: destination.url
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: destination.url
                            ? "underline"
                            : "none",
                        },
                        wordBreak: "break-all",
                      }}
                    >
                      {destination.url ||
                        t("common.noData", "No data available")}
                    </Typography>
                    {destination.url && (
                      <Tooltip title={t("common.open", "Open")}>
                        <IconButton
                          size="small"
                          component="a"
                          href={destination.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ p: 0.25 }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {destination.utm && (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <LocationSearchingIcon
                        sx={{ fontSize: 14, color: "secondary.main" }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {destination.utm}
                      </Typography>
                      <Tooltip title={t("common.copy", "Copy")}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(destination.utm)}
                          sx={{ p: 0.25 }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Grid>
  );

  const renderLinkView = (
    fieldKey: string,
    label: string,
    value?: string | Record<string, unknown> | null
  ) => {
    const iconConfig = (() => {
      switch (fieldKey) {
        case "assetsLinks":
          return {
            icon: <FolderSharedIcon fontSize="small" />,
            color: "info.main",
          };
        case "copiesLinks":
          return {
            icon: <DescriptionIcon fontSize="small" />,
            color: "secondary.main",
          };
        case "additionalLinks":
          return { icon: <LinkIcon fontSize="small" />, color: "primary.main" };
        default:
          return null;
      }
    })();

    let data: Record<string, string> = {};
    let isEmpty = true;

    if (value) {
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (typeof parsed === "object" && parsed) {
            data = parsed as Record<string, string>;
            isEmpty = Object.keys(data).length === 0;
          } else if (value.trim()) {
            data = { link: value };
            isEmpty = false;
          }
        } catch {
          if (value.trim()) {
            data = { link: value };
            isEmpty = false;
          }
        }
      } else {
        // Safely cast unknown record to string record for display
        // Assuming the backend sends strings or compatible types
        data = Object.entries(value).reduce(
          (acc, [k, v]) => {
            if (typeof v === "string") {
              acc[k] = v;
            } else {
              acc[k] = String(v);
            }
            return acc;
          },
          {} as Record<string, string>
        );
        isEmpty = Object.keys(data).length === 0;
      }
    }

    return (
      <Grid item xs={12} md={6} key={label}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            transition: "box-shadow 0.2s",
            "&:hover": { boxShadow: theme.shadows[2] },
            bgcolor: theme.palette.background.default,
          }}
        >
          {iconConfig ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              color={iconConfig.color}
              sx={{ mb: 0.5 }}
            >
              {iconConfig.icon}
              <Typography variant="subtitle2" fontWeight={600}>
                {label}
              </Typography>
            </Stack>
          ) : (
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontWeight={600}
              sx={{ mb: 0.5 }}
            >
              {label}
            </Typography>
          )}
          {isEmpty ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: "auto" }}
            >
              {t("common.noData", "No data available")}
            </Typography>
          ) : (
            <Stack spacing={0.5} sx={{ mt: "auto" }}>
              {Object.entries(data).map(([k, v]) => {
                return (
                  <Box
                    key={k}
                    sx={{ display: "flex", gap: 1, alignItems: "center" }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        minWidth: 80,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {k}:
                    </Typography>
                    <Typography
                      variant="body2"
                      component="a"
                      href={v}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: theme.palette.primary.main,
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                        wordBreak: "break-all",
                      }}
                    >
                      {v}
                    </Typography>
                    {v && v.trim().length > 0 && (
                      <Tooltip title={t("common.copy", "Copy")}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(v)}
                          sx={{ p: 0.5 }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Paper>
      </Grid>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[200]}`,
        p: { xs: 2, md: 4 },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h6" fontWeight={700}>
          {t("briefingsView.assetSectionTitle", "Assets")}
        </Typography>
        {asset ? (
          isEditMode && (
            <Tooltip title={t("common.edit", "Edit")}>
              <span>
                <IconButton
                  color="primary"
                  onClick={onEditAsset}
                  disabled={!onEditAsset}
                  sx={{ bgcolor: "action.hover" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )
        ) : (
          <Tooltip title={t("briefingsView.createAsset", "Create Asset")}>
            <span>
              <IconButton
                color="primary"
                onClick={onCreateAsset}
                disabled={!onCreateAsset}
                sx={{
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": { bgcolor: "primary.dark" },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>

      {!asset ? (
        <Alert severity="info">
          {t(
            "briefingsView.assetNotFound",
            "This associated campaign does not have asset details yet."
          )}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {renderLinkView(
            "assetsLinks",
            t("briefingsView.asset.assetsLinks", "Assets links"),
            asset.assetsLinks
          )}
          {renderLinkView(
            "copiesLinks",
            t("briefingsView.asset.copiesLinks", "Copies links"),
            asset.copiesLinks
          )}
          {renderLinkView(
            "additionalLinks",
            t("briefingsView.asset.additionalLinks", "Additional links"),
            asset.additionalLinks
          )}
          {renderDestinations(asset.destinations ?? [])}
          {renderDetailField(
            t("briefingsView.addedOn", "Added on"),
            dayjs(asset.createdAt).format("YYYY-MM-DD HH:mm:ss")
          )}
          {renderDetailField(
            t("briefingsView.updatedOn", "Last updated"),
            dayjs(asset.updatedAt).format("YYYY-MM-DD HH:mm:ss")
          )}
        </Grid>
      )}
    </Paper>
  );
};

export default AssociatedCampaignAssetSection;
